/**
 * Generic Funnel Event Tracking
 * Captures all user interactions in a flexible event log format
 *
 * Spam prevention:
 * - Field changes held until the field loses focus (one event per edit, not per keystroke)
 * - Event deduplication (skip duplicate within 1s)
 * - Rate limiting (max 200 events per session)
 * - Bot detection on server side
 */

import { trackError as trackErrorToMonitoring } from './error-tracking';
import { isAnalyticsDisabled } from './analytics-enabled';
import { normalizeFunnelEventData } from './funnel-event-data';
import { generateUUID } from './uuid';
import { getAnonymousId, getSessionId } from './tracking-context';
import { getRankingVariant } from './ranking-variant-store';

// Draft session ID (for abandonment tracking during listing creation)
let draftSessionId: string | null = null;

export function getDraftSessionId(): string {
  if (typeof window === 'undefined') return generateUUID();

  if (!draftSessionId) {
    draftSessionId = sessionStorage.getItem('draft_session_id');
    if (!draftSessionId) {
      draftSessionId = generateUUID();
      sessionStorage.setItem('draft_session_id', draftSessionId);
    }
  }
  return draftSessionId;
}

export function resetDraftSessionId(): void {
  if (typeof window !== 'undefined') {
    draftSessionId = generateUUID();
    sessionStorage.setItem('draft_session_id', draftSessionId);
  }
}
let sessionEventCount = 0;
const MAX_EVENTS_PER_SESSION = 200;

/** Server-side fallback only -- the browser's id comes from tracking-context. */
function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * The session a funnel event belongs to.
 *
 * This is the analytics session id -- the same one `events` rows carry -- and
 * that is the whole point. It used to be a private generator keyed per funnel in
 * sessionStorage, producing ids of the same *shape* as the analytics ones from
 * different storage: they looked joinable and never joined. Over 7 days, 0 of
 * 2,251 login funnel sessions had a match in `events`, so nothing about what a
 * person did before or after a funnel could be recovered.
 *
 * The `funnel` argument is kept because ~20 call sites pass it, and because a
 * funnel-scoped id is exactly the thing that must not come back.
 */
export function getFunnelSessionId(_funnel?: string): string {
  // Server-side there is no browser session to read; a fresh id keeps the insert
  // valid (session_id is a non-nullable String) without inventing continuity.
  if (typeof window === 'undefined') return generateSessionId();
  // In a browser getSessionId always answers (it mints one, or a fallback when storage throws).
  return getSessionId();
}

/**
 * No longer starts a new session, and deliberately so.
 *
 * `trackComplete` called this after every funnel completion, on the assumption
 * that the id was funnel-scoped. It is now the browser's analytics session, and
 * rotating that on a checkout would sever the person's whole visit at the point
 * they converted -- the single most useful thing in the log. A second run of the
 * same funnel is legible from its own events (`page_load`, `started`) without a
 * new id.
 *
 * Kept as a function, returning the live session, so the call sites and their
 * tests keep working.
 */
export function resetFunnelSession(_funnel?: string): string {
  return getFunnelSessionId();
}

// Event queue for batching
let eventQueue: FunnelEvent[] = [];
let flushTimeout: NodeJS.Timeout | null = null;
const FLUSH_INTERVAL = 2000; // 2 seconds
const MAX_QUEUE_SIZE = 20;

// Deduplication: track recent events to skip duplicates
const recentEvents = new Map<string, number>();
const DEDUP_WINDOW_MS = 1000; // 1 second dedup window

/**
 * Field edits waiting for the seller to leave the field.
 *
 * Keyed `funnel:field`, holding only the newest value, so a whole edit collapses
 * to the one row that describes it. `lastSentFieldValues` remembers what was
 * actually emitted, so tabbing through a field that was not changed -- or a
 * caller that re-sends the same "filled" over and over -- adds nothing.
 */
const pendingFieldChanges = new Map<
  string,
  { funnel: string; field: string; value: unknown; component?: string }
>();
const lastSentFieldValues = new Map<string, string>();

interface FunnelEvent {
  funnel: string;
  event: string;
  data?: Record<string, unknown>;
  component?: string;
  userId?: number;
  /**
   * When it happened, and on which page. Both used to be read at flush, up to
   * FLUSH_INTERVAL later, so a queued browser event sorted after the server
   * event it came before: on 2026-09-22 every `otp_entered` landed after the
   * `otp_failed` it caused, and two of them 10 s apart carried stamps 1 ms apart.
   */
  timestamp: number;
  pageUrl: string;
}

/**
 * Check if event is duplicate (same event+key fired within dedup window)
 */
/**
 * Each event's time, strictly after the one before it.
 *
 * Two events from one click share a millisecond, and ClickHouse then orders
 * them however it likes. Pay records `shipping_selected` and then
 * `payment_attempted`; on 2026-10-26 both carried 16:17:58.484 and the session
 * read attempt-before-rate. One millisecond apart keeps the order they were
 * called in, which is the order the buyer did them.
 */
let lastStamp = 0;
export function nextStamp(now = Date.now()): number {
  lastStamp = Math.max(now, lastStamp + 1);
  return lastStamp;
}

function isDuplicate(funnel: string, event: string, key?: string): boolean {
  const dedupKey = `${funnel}:${event}:${key || ''}`;
  const lastTime = recentEvents.get(dedupKey);
  const now = Date.now();

  if (lastTime && now - lastTime < DEDUP_WINDOW_MS) {
    return true;
  }

  recentEvents.set(dedupKey, now);

  // Clean old entries periodically
  if (recentEvents.size > 100) {
    for (const [k, v] of recentEvents.entries()) {
      if (now - v > DEDUP_WINDOW_MS * 2) {
        recentEvents.delete(k);
      }
    }
  }

  return false;
}

/**
 * Track a funnel event
 * Events are batched and sent every 2 seconds or when queue reaches 20 items
 *
 * Spam prevention:
 * - Rate limited to MAX_EVENTS_PER_SESSION per session
 * - Duplicate events within 1s are skipped
 */
export function trackFunnelEvent(
  funnel: string,
  event: string,
  data?: Record<string, unknown>,
  options?: { component?: string; userId?: number; immediate?: boolean; dedupKey?: string }
) {
  if (typeof window === 'undefined') return;
  if (isAnalyticsDisabled()) return;

  // Rate limiting
  if (sessionEventCount >= MAX_EVENTS_PER_SESSION) {
    return;
  }

  // Deduplication (skip for errors and completion which should always go through)
  if (event !== 'error' && event !== 'complete') {
    if (isDuplicate(funnel, event, options?.dedupKey)) {
      return;
    }
  }

  sessionEventCount++;

  const funnelEvent: FunnelEvent = {
    funnel,
    event,
    data,
    component: options?.component,
    userId: options?.userId,
    timestamp: nextStamp(),
    pageUrl: window.location.href,
  };

  if (options?.immediate) {
    // Send immediately (for errors, completion)
    void sendEvents([funnelEvent]);
  } else {
    // Add to queue
    eventQueue.push(funnelEvent);

    // Flush if queue is full
    if (eventQueue.length >= MAX_QUEUE_SIZE) {
      flushEvents();
    } else if (!flushTimeout) {
      // Schedule flush
      flushTimeout = setTimeout(flushEvents, FLUSH_INTERVAL);
    }
  }
}

function enrichEvents(events: FunnelEvent[]) {
  return events.map((e) => ({
    session_id: getFunnelSessionId(),
    // The stable per-browser id, so a flow can be tied to the same visitor
    // across sessions -- an abandoned login on Monday and the signup on Tuesday.
    anonymous_id: getAnonymousId(),
    // Which ranking arm served this visit. `events` has carried it since the
    // experiment mechanism shipped; without it here, the checkout rungs -- the
    // only ones with the volume to test anything soon -- cannot be split by arm.
    ranking_variant: getRankingVariant(),
    funnel: e.funnel,
    event: e.event,
    data: JSON.stringify(normalizeFunnelEventData(e.data)),
    component: e.component || '',
    user_id: e.userId || null,
    page_url: e.pageUrl,
    timestamp: e.timestamp,
  }));
}

function flushEvents(useBeacon = false) {
  if (flushTimeout) {
    clearTimeout(flushTimeout);
    flushTimeout = null;
  }

  if (eventQueue.length === 0) return;

  const eventsToSend = [...eventQueue];
  eventQueue = [];

  if (useBeacon) {
    sendEventsBeacon(eventsToSend);
  } else {
    void sendEvents(eventsToSend);
  }
}

// Both senders are only handed a non-empty batch (flushEvents returns on an empty queue).
function sendEventsBeacon(events: FunnelEvent[]) {
  const body = JSON.stringify({ events: enrichEvents(events) });
  navigator.sendBeacon('/api/analytics/funnel/track', new Blob([body], { type: 'application/json' }));
}

async function sendEvents(events: FunnelEvent[]) {
  try {
    await fetch('/api/analytics/funnel/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: enrichEvents(events) }),
    });
  } catch (err) {
    trackErrorToMonitoring(err instanceof Error ? err : String(err), undefined, { source: 'track-funnel', operation: 'sendEvents' } as unknown as Parameters<typeof trackErrorToMonitoring>[2]);
  }
}

// Flush on page unload — use sendBeacon so the request isn't cancelled during teardown
if (typeof window !== 'undefined') {
  const teardown = () => {
    flushFieldChanges();
    flushEvents(true);
  };
  window.addEventListener('beforeunload', teardown);
  window.addEventListener('pagehide', teardown);
  /**
   * One delegated listener instead of an `onBlur` on every input.
   *
   * `focusout` bubbles, so this catches leaving any field on any funnel page,
   * including the sell wizard, whose tracking is driven from state in
   * SellProvider and has no blur handlers of its own to hang this on.
   */
  window.addEventListener('focusout', () => flushFieldChanges());
  // Backgrounding a tab on mobile is the end of the edit as often as a blur is.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') teardown();
  });
}

// ============================================================================
// Convenience helpers for common events
// ============================================================================

/**
 * Record a field's new value. Nothing is sent until the field loses focus.
 *
 * This used to be a 500ms debounce, which only ever collapsed a fast burst. A
 * seller typing a listing title at a normal, thinking pace pauses longer than
 * that between words, so one title produced a row per word: on 2026-09-22 a
 * single sell session logged `title` seven times ("A", "Air ", "Air Jordan ",
 * "Air Jordan 3 ", ...) and `description` four, for two fields. That is noise
 * on the timeline and it spends `MAX_EVENTS_PER_SESSION` on prefixes of a value
 * whose final form is the only one worth having.
 *
 * Blur is the natural end of an edit, so the value is held and emitted by
 * `flushFieldChanges` -- on focusout, on page teardown, and on completion.
 */
export function trackFieldChange(funnel: string, field: string, value: unknown, component?: string) {
  if (typeof window === 'undefined') return;

  pendingFieldChanges.set(`${funnel}:${field}`, { funnel, field, value, component });
}

/**
 * Emit one `field_change` per field the seller actually changed.
 *
 * Safe to call on every focusout: with nothing pending it does nothing, and a
 * value identical to the last one sent for that field is dropped rather than
 * repeated.
 */
export function flushFieldChanges(): void {
  if (pendingFieldChanges.size === 0) return;

  const pending = [...pendingFieldChanges.values()];
  pendingFieldChanges.clear();

  for (const { funnel, field, value, component } of pending) {
    const key = `${funnel}:${field}`;
    const serialized = JSON.stringify(value ?? null);
    if (lastSentFieldValues.get(key) === serialized) continue;
    lastSentFieldValues.set(key, serialized);
    trackFunnelEvent(funnel, 'field_change', { field, value }, { component, dedupKey: field });
  }
}

/** Track button/link clicks (deduplicated by target) */
export function trackClick(funnel: string, target: string, data?: Record<string, unknown>, component?: string) {
  trackFunnelEvent(funnel, 'click', { target, ...data }, { component, dedupKey: target });
}

/** Track errors (sent immediately) */
export function trackError(funnel: string, type: string, message: string, data?: Record<string, unknown>) {
  trackFunnelEvent(funnel, 'error', { type, message, ...data }, { immediate: true });
}

/** Track funnel completion */
export function trackComplete(funnel: string, data?: Record<string, unknown>) {
  // Submitting by keyboard never blurs the last field; its edit ships here.
  flushFieldChanges();
  trackFunnelEvent(funnel, 'complete', data, { immediate: true });
  // Reset session for next funnel attempt
  resetFunnelSession(funnel);
}

/** Track page/component load (deduplicated by page) */
export function trackPageLoad(funnel: string, page: string, data?: Record<string, unknown>) {
  trackFunnelEvent(funnel, 'page_load', { page, ...data }, { dedupKey: page });
}

/**
 * Track API request (sent immediately, deduplicated by endpoint)
 *
 * The dedup key matters more here than anywhere else in this file. Without one
 * `isDuplicate` keys on `<funnel>:api_request:` alone, so ANY two API events in
 * the same funnel within a second collapse into the first -- and API calls come
 * in bursts by nature. Every other helper here already passes one; these two
 * were the omission.
 *
 * It cost real steps on production. `confirmPhotos` reports `publish_listing`
 * and then `confirm_photos` back to back, and over 30 days the table held 293
 * `publish_listing` responses and not one `confirm_photos`. `handleSaveSuccess`
 * reports `create_listing` success immediately after, which had 0 rows against
 * 255 publishes -- so the selling funnel's Draft Saved node read zero for its
 * whole existence while the listings it counts were being created normally.
 */
export function trackApiRequest(funnel: string, endpoint: string, data?: Record<string, unknown>) {
  trackFunnelEvent(funnel, 'api_request', { endpoint, ...data }, { immediate: true, dedupKey: endpoint });
}

/**
 * Track API response (sent immediately, deduplicated by endpoint AND outcome)
 *
 * The outcome belongs in the key because the failure path reports two responses
 * for one call -- `publish_listing` false then `confirm_photos` false -- and
 * because a retry that succeeds within a second of failing is the most
 * interesting thing this event records.
 */
export function trackApiResponse(
  funnel: string,
  endpoint: string,
  success: boolean,
  data?: Record<string, unknown>
) {
  trackFunnelEvent(
    funnel,
    'api_response',
    { endpoint, success, ...data },
    { immediate: true, dedupKey: `${endpoint}:${success}` },
  );
}

/** Track user-facing validation message */
export function trackValidationMessage(
  funnel: string,
  field: string,
  message: string,
  value?: unknown
) {
  trackFunnelEvent(funnel, 'validation_message', { field, message, value }, { dedupKey: `${field}:${message}` });
}

/** Track funnel abandoned (user started but didn't complete) */
export function trackAbandoned(funnel: string, data?: Record<string, unknown>) {
  trackFunnelEvent(funnel, 'abandoned', data, { immediate: true });
}

/** Track modal/page opened (informational, doesn't start funnel) */
export function trackModalOpened(funnel: string, data?: Record<string, unknown>) {
  trackFunnelEvent(funnel, 'modal_opened', data);
}

/** Track modal/page closed */
export function trackModalClosed(funnel: string, data?: Record<string, unknown>) {
  trackFunnelEvent(funnel, 'modal_closed', data);
}
